<template>
  <div id="dashboard-teamlist">
    <BackButton />

    <div class="row">
      <b-col sm="11" class="name-col">
        <h1 :class="{notset: !name}">{{name|| '(no name)'}}</h1>
        <span class="role-badge">{{userRoleText()}}</span>
        <span class="invite-fineprint" v-if="!member.is_active">
          <i class="fa fa-envelope" />
          <span>Invite sent!</span>
        </span>
      </b-col>
      <b-col sm=1>
        <b-dropdown v-if="showActionDropdown()" size="sm" variant="clear" right no-caret>
          <template slot="button-content"><i class="fas fa-ellipsis-v"/></template>
          <b-dropdown-item v-if="showChangeRole()" @click="onClickChangeRole">Change role to {{otherRole()}}</b-dropdown-item>
          <b-dropdown-item v-if="showRemoveFromTeam()" @click="onClickRemoveFromTeam">Remove from team</b-dropdown-item>
          <b-dropdown-item v-if="showDelete()" @click="onClickDelete">Delete User</b-dropdown-item>
          <b-dropdown-item v-if="showImpersonate()" @click="onClickImpersonate">Log In As {{member.username}}</b-dropdown-item>
        </b-dropdown>
      </b-col>
    </div>
    <div class="profile-and-actions">
      <ProfileRow class="profile" :member-id.number="id" :key="member.id" />
      <div class="actions">
        <b-button v-if="showResend()" @click="onClickResend">Resend Invite</b-button>
        <StartOrderButton :member="member" v-if="showStartOrder()" />
      </div>
    </div>
    <div>
      <AdminPreferences :key="'adminprefs'+member.id" />
      <MemberRestrictions :key="member.id" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '~scss/colors';

.name-col {
  padding-bottom: 1em;
  h1 {
    padding: 0; margin: 0;

    &.notset {
      color: #aaa;
    }
  }
  .role-badge {
    background-color: #ccc;
    color: white;
    padding: 2px 4px;
    text-transform: uppercase;
    font-size: small;
    line-height: 1.5em;
  }
  .invite-fineprint {
    // Make the envelope line up nicely with the badge
    position: relative;
    top: 2px;

    padding-left: 6px;
    line-height: 1.5em;
    font-size: medium;
    color:#048fc2;
  }
}
td {
  vertical-align: center;
}
.invited {
  color: #ccc;
}
.invite-fineprint {
  font-size: medium;
  color:#048fc2;
}

.profile-and-actions {
  display: flex;
  flex-flow: row nowrap;
  .profile {
    max-width: 35em;
    min-width: 20em;
  }
  .actions {
    margin-left: 2em;
    margin-top: 1em;
    > * {
      margin-bottom: 1em;
    }
  }

  @media screen and (max-width: 850px) {
    flex-flow: row wrap-reverse;
    .actions {
      margin-top: 0;
      margin-left: 0;
      > * {
        margin-bottom: 0;
        margin-right: 1em;
      }
    }
  }
}

</style>

<script>
import { mapGetters, mapActions } from "vuex";
import wrapAction from "@/lib/bubble/wrapAction";
import ProfileRow from "./ProfileRow";
import MemberRestrictions from "./MemberRestrictions";
import AdminPreferences from "./AdminPreferences";

export default {
  components: {
    ProfileRow,
    MemberRestrictions,
    AdminPreferences
  },
  props: ['id'],
  data() {
    return {sending: false};
  },
  computed: {
    ...mapGetters(["flags", "member", "user", "isAdmin", "impersonating", "isStaff"]),
    name() {
      let {first_name, last_name} = this.member;
      if (first_name || last_name) return (first_name + ' ' + last_name).trim();
      return null;
    }
  },
  watch: {
    id(val) {
      this.loadTeamMember(this.id);
    }
  },
  methods: {
    ...mapActions(['resendInvite', 'loadTeamMember', 'deleteUser', 'updateUserProfile', 'removeFromTeam', 'impersonate']),
    async onClickResend() {
      this.sending = true;
      try {
        await Promise.all([
          this.resendInvite(this.member.email),
          new Promise(r => setTimeout(r, 100)) // wait a little bit so user can notice something happened
        ]);
        this.sending = false;
      } catch (e) {
        // By far most likely cause is user has accepted the invite.
        // Refreshing the list should help inform the user of this fact.
        // TODO implement other error handling
        this.reloadTeamInfo();
        console.error(e);
      }
    },

    showActionDropdown() {
      // Hide if no actions are available
      return this.showResend() || this.showChangeRole() || this.showRemoveFromTeam() || this.showDelete() || this.showImpersonate();
    },

    showResend() {
      // Only for inactive users.
      return !this.member.is_active;
    },

    showChangeRole() {
      // Only show if the user is active and has a user_type we understand.
      // Can't change your own role. Must be logged in as admin.
      return this.isAdmin && this.otherRole() && this.member.is_active && this.member.id !== this.user.id;
    },
    onClickChangeRole: wrapAction({
      async action() {
        let user_type = {admin: 'user', user: 'admin'}[this.member.user_type];
        if (!user_type) throw "Invalid user type";
        let id = this.member.id;
        // TODO show in process
        await this.updateUserProfile({id, user_type});
        // TODO message for error/success
      },
      success: "Role changed successfully"
    }),

    showRemoveFromTeam() {
      // Can't remove yourself. Must be admin, Can remove active or inactive members.
      return this.isAdmin && this.member.id !== this.user.id;
    },
    onClickRemoveFromTeam: wrapAction({
      confirm: 'Remove team member?',
      action: 'removeFromTeam',
      arg: {data: 'member'},
      success: 'Team member removed'
    }),

    showDelete() {
      // Dangerous operation -- hidden behind flag & can't delete yourself
      return this.flags.enableDeleteUser && this.user.is_staff && this.member.id !== this.user.id;
    },
    onClickDelete: wrapAction({
      confirm() { return `Permanently delete ${this.member.email}?`; },
      action: 'deleteUser',
      arg() { return this.member.id; },
      success: 'Success'
    }),

    showImpersonate() {
      return this.impersonating && this.isStaff;
    },
    async onClickImpersonate() {
      // Once this completes, the browser should be logged in as user.
      await this.impersonate(this.member.username);
      // To avoid issues with state not being completely updated,
      // just reload.
      window.location.reload();
    },

    showStartOrder() {
      // No reason this can't apply for invitees
      return true;
    },

    userRoleText() {
      // Return a label for the user type. If we get an unexpected user_type, just return that.
      return {admin: 'Admin', user: 'Orderer'}[this.member.user_type] || this.member.user_type;
    },
    otherRole() {
      // Return the OPPOSITE type. If we get an unexpected user_type this returns undefined.
      return {admin: 'Orderer', user: 'Administrator'}[this.member.user_type];
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/pages/dashboard/team/MemberDetail.vue